import { useState } from "react";
import { useParams } from "react-router";
import { personImText, type Anrede, type Rolle } from "@dj/domain/gemeinsam";
import { Dialog } from "../design/Dialog";
import { Auswahl } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { meldungsIdAnzeige } from "../design/Kennung";
import { Symbol } from "../design/Symbol";
import { befehl } from "../plattform/api";
import type { Zeile } from "../plattform/lesemodelle";
import { dateiHerunterladen, useEigeneLage, useRevierteil, wert, type Rahmen } from "./daten";

/**
 * Ansicht „Jagddokumentation“ (oberflaeche.md §3.2, §6; F-192): je Teil eine `region` — „Verlauf des Jagdzustands“
 * (Tabelle `Zeitpunkt`, `Eintrag`, `Jagdzustand`, `Grund`), „Streckenliste“, „Schüsse und Suchen“, „Sichtungen“,
 * „Prüfprotokoll“, „Rückmeldungen“ — und „Exportieren“. Personen im Fließtext des Verlaufs nach ihrer Anrede
 * (oberflaeche.md §2.1, §2.2: „Hahn in Ruh ausgerufen von Jagdleiter Bernd Hollmann“, ohne Anrede „…, leitet die Jagd“);
 * in den Tabellen nur mit Namen; eine anonyme Rückmeldung als „anonym“ (lesemodelle.md §1.5).
 */

interface Person { Kennung: string; Name: string; Anrede: Anrede }

/** Eintrag des Verlaufs: Eingriffe und Zeitplanänderungen mit der Person im Fließtext; das erste Festlegen gehört zur Planung. */
export function verlaufseintrag(z: Zeile): string {
  const eintrag = String(z["Eintrag"] ?? "");
  const person = z["Person"] as Person | null | undefined;
  if (!person?.Name) return eintrag;
  return `${eintrag} von ${personImText(person.Name, ((z["Rolle"] as Rolle | null) ?? "Jagdleiter"), person.Anrede)}`;
}

function Teil({ name, symbol, spalten, zeilen }: { name: string; symbol: string; spalten: string[]; zeilen: unknown[][] }) {
  return (
    <section className="liste-bereich platte" role="region" aria-label={name}>
      <h2 className="kopf"><Symbol name={symbol} />{name}</h2>
      <table className="tabelle">
        <thead><tr>{spalten.map((s) => <th key={s} scope="col">{s}</th>)}</tr></thead>
        <tbody>{zeilen.map((z, i) => <tr key={i}>{z.map((w, j) => <td key={j}>{wert(w)}</td>)}</tr>)}</tbody>
      </table>
    </section>
  );
}

export function Jagddokumentation({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const { revier } = useEigeneLage(id);
  const verlauf = useRevierteil(id, revier, "jagddokumentation_jagdzustand").filter((z) => z["Eintrag"] !== "Jagdzeitplan festgelegt");
  const streckenliste = useRevierteil(id, revier, "streckenliste");
  const schuesse = useRevierteil(id, revier, "jagddokumentation_schuesse");
  const sichtungen = useRevierteil(id, revier, "jagddokumentation_sichtungen").filter((z) => z["storniert"] !== true);
  const pruefprotokoll = useRevierteil(id, revier, "pruefprotokoll");
  const rueckmeldungen = useRevierteil(id, revier, "jagddokumentation_rueckmeldungen");
  const [offen, setOffen] = useState(false);
  const [format, setFormat] = useState("PDF");
  const [grund, setGrund] = useState<string | null>(null);

  async function exportieren() {
    setOffen(false);
    setGrund(null);
    const a = await befehl("JagddokumentationExportieren", { "Drückjagd": id, ...(revier ? { Revier: revier } : {}), Format: format });
    const datei = (a.felder["datei"] as { url?: string } | undefined)?.url;
    const fehler = a.ok && datei ? await dateiHerunterladen(datei) : (a.grund ?? "unzulaessige-angaben");
    if (fehler) setGrund(fehler);
  }

  const unten = (
    <div className="raster raster--1 liste__unten">
      <Kachel art="kompakt" symbol="herunterladen" begriff="Exportieren" onPress={() => { setGrund(null); setOffen(true); }} />
    </div>
  );
  return rahmen({
    drueckjagdId: id, name: "Jagddokumentation", unten,
    children: (
      <>
        <Abweisung grund={grund} />
        <Teil name="Verlauf des Jagdzustands" symbol="hahn-in-ruh" spalten={["Zeitpunkt", "Eintrag", "Jagdzustand", "Grund"]}
          zeilen={verlauf.map((z) => [z["Zeitpunkt"], verlaufseintrag(z), z["Jagdzustand"], z["Grund"]])} />
        <Teil name="Streckenliste" symbol="strecke" spalten={["Wildart", "Ansprache", "Erlegt durch", "Datum", "Trichinenprobennummer", "Wildmarke"]}
          zeilen={streckenliste.map((z) => [z["Wildart"], z["Ansprache"], z["Erlegt durch"], z["Datum"], z["Trichinenprobennummer"], z["Wildmarke"]])} />
        <Teil name="Schüsse und Suchen" symbol="schuss" spalten={["Schuss", "Gemeldet durch", "Stand", "Zeitpunkt", "Ansprache", "Schussausgang", "Schützenposition"]}
          zeilen={schuesse.map((z) => [meldungsIdAnzeige(z["Schuss"]), z["Gemeldet durch"], z["Stand"], z["Zeitpunkt"], z["Ansprache"], z["Schussausgang"], z["Schützenposition"]])} />
        <Teil name="Sichtungen" symbol="wildsichtung" spalten={["Zeitpunkt", "Gemeldet durch", "Wildart", "Anzahl", "Bewegungsrichtung"]}
          zeilen={sichtungen.map((z) => [z["Zeitpunkt"], z["Gemeldet durch"], z["Wildart"], z["Anzahl"], z["Bewegungsrichtung"]])} />
        <Teil name="Prüfprotokoll" symbol="jagdschein" spalten={["geprüft am", "Geprüft durch", "Person", "Prüfung", "Ergebnis", "gültig bis", "Grund"]}
          zeilen={pruefprotokoll.map((z) => [z["geprüft am"], z["Geprüft durch"], z["Person"], z["Prüfung"], z["Ergebnis"], z["gültig bis"], z["Grund"]])} />
        <Teil name="Rückmeldungen" symbol="nachricht" spalten={["Verfasst durch", "Bewertung", "Was war gut?", "Was verbessern?"]}
          zeilen={rueckmeldungen.map((z) => [z["Verfasst durch"] ? z["Verfasst durch"] : "anonym", z["Bewertung"], z["Was war gut?"], z["Was verbessern?"]])} />
        <Dialog name="Exportieren" symbol="herunterladen" offen={offen} onSchliessen={() => setOffen(false)}>
          <Auswahl label="Format" optionen={["PDF", "CSV"]} wert={format} onChange={setFormat} />
          <div className="knopfreihe">
            <Knopf symbol="herunterladen" onPress={() => void exportieren()}>Speichern</Knopf>
          </div>
        </Dialog>
      </>
    ),
  });
}
